import { useCallback } from 'react';
import SweetAlert from '@components/Feedback/SweetAlert/Index.jsx';
import { UpdateSolicitacao, SendSolicitacao, SendReportSolicitacao, FinishSolicitacao } from '@services/Solicitacao/Index.jsx';
import { useDispatch } from 'react-redux';
import { atualizarStatus } from '@store/slices/solicitacaoSlice.js';
import { addHistorico } from '@store/slices/solicitacaoHistoricoSlice.js';
import ValidationsEditSolicitacao from '@validations/Solicitacao/Admin/Edit/Validations.jsx';
import { formatDateToBR } from '@utils/FormatDate';
import { useFormContext } from 'react-hook-form';

export const useSolicitacaoSubmitHandlers = ({ solicitacaoFields, solicitacaoProcessoItems }) => {
  const dispatch = useDispatch();
  const NumeroSolicitacao = solicitacaoFields?.numeroSolicitacao;
  const { getValues } = useFormContext();


  // Função para atualizar a solicitação
  const onUpdate = useCallback(async (data) => {
    const title = "Atualização da Solicitação";
    const htmlMessage = `Deseja realmente atualizar a solicitação de número <strong>${NumeroSolicitacao}</strong>?`;

    const confirmUpdate = await SweetAlert.warningHTML(title, htmlMessage);
    if (!confirmUpdate) return;

    try {
      const { validateFields } = ValidationsEditSolicitacao();
      const firstError = await validateFields(data, solicitacaoProcessoItems);

      if (firstError) {
        SweetAlert.error("Erro ao Atualizar", firstError.message);
        return;
      }

      SweetAlert.loading();

      const updatedPayload = {
        statusSolicitacao: solicitacaoFields.statusSolicitacao,
        idTemplateEmailComunicado: data.templateEmailComunicado.value || null,
        idTemplateEmailSolicitacao: data.templateEmailSolicitacao.value || null,
        idTemplateEmailRelatorio: data.templateEmailRelatorio.value || null,
        emailCC: data.emailCC || null,
        prazoSolicitacao: data.prazoSolicitacao,
        dataAgendarEnvio: data.dataAgendarEnvio || null,
        dataLimiteSolicitacao: data.dataLimiteSolicitacao,
        observacoes: data.observacoes || null,
        idAuditor: data.nomeAuditor.value,
        solicitacaoProcessoItems,
      };

      console.log("Payload que está sendo enviado:", JSON.stringify(updatedPayload, null, 2));

      const result = await UpdateSolicitacao(solicitacaoFields.id, updatedPayload);

      if (result?.solicitacaoHistorico) {
        dispatch(addHistorico(result.solicitacaoHistorico));
        SweetAlert.success('Atualização Concluída!', 'Os dados da solicitação foram atualizados com sucesso.');
      } else {
        SweetAlert.error('Erro na Atualização', 'Não foi possível registrar o histórico da atualização.');
      }
    } catch (error) {
      const errorMessage = error.parsed?.message || 'Não foi possível atualizar a solicitação.';

      SweetAlert.errorHTML('Erro ao atualizar', errorMessage);
    }
  }, [solicitacaoFields, solicitacaoProcessoItems]);



  // Funções para manipular o envio da solicitação
  const onSubmitRequest = useCallback(async () => {

    const { dataAgendarEnvio } = getValues();
    const horarioAgendamentoEnvio =  import.meta.env.VITE_API_SCHEDULED_SEND_TIME;

    if (dataAgendarEnvio) {
      const title = "Agendamento do Envio";

      const message = `Deseja realmente agendar o envio desta solicitação para 
      <strong>${formatDateToBR(dataAgendarEnvio)}, ${horarioAgendamentoEnvio}</strong> ? <br>
      O horário é predefinido pelo sistema.`

      const confirmAgendamento = await SweetAlert.warningHTML(title, message);

      if (!confirmAgendamento) return;
    } else {
      const title = "Envio da Solicitação";
      const htmlMessage = `Deseja realmente enviar a solicitação de número <strong>${NumeroSolicitacao}</strong>?`;

      const confirm = await SweetAlert.warningHTML(title, htmlMessage);
      if (!confirm) return;
    }

    SweetAlert.loading();

    try {
      const result = await SendSolicitacao(solicitacaoFields.id);
      if (!result || typeof result.actions.statusSolicitacao === 'undefined') {
        SweetAlert.error('Erro ao Enviar', 'O servidor não retornou um status válido.');
        return;
      }
      dispatch(atualizarStatus({ id: solicitacaoFields.id, status: result.actions.numeroStatusSolicitacao }));
      dispatch(addHistorico(result.actions.solicitacaoHistorico));

      const title = result.notification.title || 'Solicitação Processada';
      const subtitle = result.notification.subtitle || 'Solicitação processada com sucesso.';

      // Exibe alerta com titulo e subtitulo que vem da api
      SweetAlert.success(title, subtitle);

    } catch (error) {
      const errorMessage = error.parsed?.message || 'Não foi possível enviar a solicitação.';

      SweetAlert.errorHTML('Erro ao enviar', errorMessage);
    }
  }, [solicitacaoFields]);



  // Função para enviar o relatório da solicitação
  const onSubmitReport = useCallback(async () => {
    const title = "Envio do Relatório da Solicitação";
    const htmlMessage = `Deseja realmente enviar o relatório da solicitação de número <strong>${NumeroSolicitacao}</strong>?`;

    const confirm = await SweetAlert.warningHTML(title, htmlMessage);
    if (!confirm) return;

    const { validateTemplateEmailRelatorio, validateAnexoRelatorio } = ValidationsEditSolicitacao();

    const errorAnexo = await validateAnexoRelatorio(
      solicitacaoFields?.compliance?.idProcesso,
      solicitacaoFields?.unidadeGerencia?.id
    );

    if (errorAnexo) {
      SweetAlert.errorHTML("Erro ao Enviar Relatório", errorAnexo.html);
      return;
    }

    const { templateEmailRelatorio } = getValues();
    const errorTemplate = await validateTemplateEmailRelatorio(templateEmailRelatorio);

    if (errorTemplate) {
      SweetAlert.error("Erro ao Enviar Relatório", errorTemplate.message);
      return;
    }

    SweetAlert.loading();

    try {
      const result = await SendReportSolicitacao(solicitacaoFields.id, templateEmailRelatorio.value);


      if (!result || typeof result.statusSolicitacao === 'undefined') {
        SweetAlert.error('Erro ao Enviar Relatório', 'O servidor não retornou um status válido.');
        return;
      }

      dispatch(atualizarStatus({ id: solicitacaoFields.id, status: result.numeroStatusSolicitacao }));

      dispatch(addHistorico(result.solicitacaoHistorico));

      SweetAlert.success(
        'Relatório Enviado!',
        'O e‑mail de notificação foi encaminhado ao Gerente e ao Encarregado da respectiva Unidade/Gerência.');

    } catch (error) {
      const errorMessage = error.parsed?.message || 'Não foi possível enviar o relatório da solicitação.';

      SweetAlert.errorHTML('Erro ao enviar relatório', errorMessage);
    }
  }, [solicitacaoFields]);



  // Função para encerrar a solicitação
  const onFinishRequest = useCallback(async () => {
    const title = "Encerramento da Solicitação";
    const htmlMessage = `Deseja realmente encerrar a solicitação de número <strong>${NumeroSolicitacao}</strong>?`;

    const confirm = await SweetAlert.warningHTML(title, htmlMessage);
    if (!confirm) return;

    SweetAlert.loading();

    try {
      const result = await FinishSolicitacao(solicitacaoFields.id);
      if (!result || typeof result.statusSolicitacao === 'undefined') {
        SweetAlert.error('Erro ao Encerrar', 'O servidor não retornou um status válido.');
        return;
      }
      dispatch(atualizarStatus({ id: solicitacaoFields.id, status: result.numeroStatusSolicitacao }));
      dispatch(addHistorico(result.solicitacaoHistorico));

      SweetAlert.success(
        'Solicitação Encerrada!',
        'O e‑mail de notificação foi encaminhado ao Gerente e ao Encarregado da respectiva Unidade/Gerência.');

    } catch (error) {
      const errorMessage = error.parsed?.message || 'Não foi possível encerrar a solicitação.';

      SweetAlert.errorHTML('Erro ao encerrar', errorMessage);
    }
  }, [solicitacaoFields]);

  return {
    onUpdate,
    onSubmitRequest,
    onSubmitReport,
    onFinishRequest
  };
};
